import * as _ from 'lodash';
import * as React from 'react';

import { MFAFactor } from '@journeyapps-platform/types-accounts-hub';

import { useSystem } from '../../../../hooks/useSystem';
import { useMFAStore } from '../../../../hooks/useMFAStore';

import { MFAProviderSelectorWidget } from './MFAProviderSelectorWidget';
import { wrapLoadingAction } from '../../../../utils/actions';
import { SMSMFAConfigureWidget } from './factors/sms/SMSMFAConfigureWidget';
import { AuthenticatorConfigureWidget } from './factors/authenticator/AuthenticatorMFAConfigureWidget';
import { AuthenticatorMFAModel } from '../../../../models/AuthenticatorMFAModel';
import { SMSMFATestWidget } from './factors/sms/SMSMFATestWidget';
import { AuthenticatorMFATestWidget } from './factors/authenticator/AuthenticatorMFATestWidget';

export enum SetupMFAStep {
  SELECT_TYPE = 'Select type',
  CONFIGURE = 'Configure',
  TEST = 'Test'
}

export const SetupMFASteps = _.values(SetupMFAStep);

export type ConfigureMFADialogProps = {
  onSubmitOrCancel: () => void;
};

export type ConfigureMFANavigationAction = (
  event?: React.MouseEvent,
  loading?: (isLoading: boolean) => void
) => Promise<void | null>;

type Payload = {
  currentStep: SetupMFAStep;
  factorId: string | null;
  factorType: MFAFactor | null;
};

export const ConfigureMFADialog: React.FC<ConfigureMFADialogProps> = (props) => {
  const { onSubmitOrCancel } = props;

  const system = useSystem();

  const mfaStore = useMFAStore();

  const [payload, setPayload] = React.useState<Payload>({
    factorId: null,
    factorType: null,
    currentStep: SetupMFAStep.SELECT_TYPE
  });

  const [hideDialog, setHideDialog] = React.useState(true);

  const { factorId, factorType, currentStep } = payload;
  const factorModel = factorId ? mfaStore.item(factorId) : null;

  const wrappedOnSubmitOrCancel =
    (nextAction: () => void) => (event?: React.MouseEvent, loading?: (isLoading: boolean) => void) =>
      wrapLoadingAction(
        async () => {
          const confirmation = await system.dialogStore.confirmDialog({
            message: 'Are you sure you want to discard changes?'
          });
          if (!confirmation) {
            return;
          }

          await factorModel?.delete();
          await mfaStore.loadUserMFAConfig();
          nextAction();
        },
        loading ?? (() => {}),
        (error) => {
          system.dialogStore.errorDialog({
            errorMessage: 'Could not discard changes. See console for details.'
          });
        }
      );

  const onSubmit = async () => {
    try {
      await factorModel?.setAsPreferred();
      await system.dialogStore.infoDialog({
        message: `Successfully configured ${factorModel?.name} multi-factor authentication.`
      });
      onSubmitOrCancel();
    } catch (error) {
      console.error(error);
      return system.dialogStore.errorDialog({
        errorMessage: `Could not set ${factorModel?.name} as primary multi-factor authentication method.`
      });
    }
  };

  // Load existing state
  React.useEffect(() => {
    // Ask for authentication immediately if not yet authenticated
    system.clientProvisioner
      .getSensitiveAccountsToken()
      .then((token) => {
        setHideDialog(false);
      })
      .catch((error) => {
        console.error(error);
        // Close
        onSubmitOrCancel();
      });
    mfaStore.loadUserMFAConfig().then(() => mfaStore.cleanupPendingFactors());
  }, []);

  if (hideDialog) {
    return <></>;
  }

  switch (currentStep) {
    case SetupMFAStep.SELECT_TYPE:
      return (
        <MFAProviderSelectorWidget
          onCancel={onSubmitOrCancel}
          onConfigure={async (factorType) => {
            if (factorType == MFAFactor.TOTP) {
              // Create the factor
              try {
                const client = await system.clientProvisioner.provisionSensitiveUserHubClient();
                const response = await client.registerTOTPUserMFAFactor();
                const model = mfaStore.upsert(response.factor) as AuthenticatorMFAModel;
                model.factorURI = response.factor_uri;
                payload.factorId = model.id;
              } catch (error) {
                return system.dialogStore.errorDialog({
                  errorMessage:
                    'Could not configure Authenticator multi-factor authentication. See console for details.'
                });
              }
            }

            setPayload({
              ...payload,
              factorType,
              currentStep: SetupMFAStep.CONFIGURE
            });
          }}
        />
      );
    case SetupMFAStep.CONFIGURE:
      switch (factorType) {
        case MFAFactor.SMS:
          return (
            <SMSMFAConfigureWidget
              factorId={factorId}
              back={wrappedOnSubmitOrCancel(() => {
                setPayload({
                  ...payload,
                  currentStep: SetupMFAStep.SELECT_TYPE
                });
              })}
              next={(factorId) => {
                setPayload({
                  ...payload,
                  factorId,
                  currentStep: SetupMFAStep.TEST
                });
              }}
              onCancel={wrappedOnSubmitOrCancel(() => onSubmitOrCancel())}
            />
          );
        case MFAFactor.TOTP:
          return (
            <AuthenticatorConfigureWidget
              factorId={factorId!}
              back={wrappedOnSubmitOrCancel(() => {
                setPayload({
                  ...payload,
                  currentStep: SetupMFAStep.SELECT_TYPE
                });
              })}
              next={async () => {
                setPayload({
                  ...payload,
                  currentStep: SetupMFAStep.TEST
                });
              }}
              onCancel={wrappedOnSubmitOrCancel(() => onSubmitOrCancel())}
            />
          );
      }
    case SetupMFAStep.TEST:
      switch (factorType) {
        case MFAFactor.SMS:
          return (
            <SMSMFATestWidget
              factorId={factorId!}
              back={async () => {
                setPayload({
                  ...payload,
                  currentStep: SetupMFAStep.CONFIGURE
                });
              }}
              next={onSubmit}
              onCancel={wrappedOnSubmitOrCancel(() => onSubmitOrCancel())}
            />
          );
        case MFAFactor.TOTP:
          return (
            <AuthenticatorMFATestWidget
              factorId={factorId!}
              back={async () => {
                setPayload({
                  ...payload,
                  currentStep: SetupMFAStep.CONFIGURE
                });
              }}
              next={onSubmit}
              onCancel={wrappedOnSubmitOrCancel(() => onSubmitOrCancel())}
            />
          );
      }
  }

  return <></>;
};
